import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CheckCircle,
  Clock,
  MagnifyingGlass,
  Package,
  Plus,
  Wrench,
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

import {
  gmao,
  gmaoInstallationWorkflow,
  machineCatalog as machineCatalogApi,
} from "@/api";
import InstallationCreateModal from "@/components/gmao/InstallationCreateModal";
import InstallationKanbanColumn from "@/components/gmao/InstallationKanbanColumn";
import MachineFormModal from "@/components/gmao/MachineFormModal";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  INSTALLATION_STAGE,
  INSTALLATION_STAGE_CONFIG,
} from "@/lib/gmaoInstallationStatus";
import { THEME_COLORS } from "@/lib/themeColors";


const PAGE_SIZE = 6;

const STAGE_PRESENTATION = {
  [INSTALLATION_STAGE.PREPARE]: {
    color: THEME_COLORS.orange,
    icon: Package,
  },
  [INSTALLATION_STAGE.CONFIRMATION]: {
    color: THEME_COLORS.orange,
    icon: Clock,
  },
  [INSTALLATION_STAGE.PROGRESS]: {
    color: THEME_COLORS.primary,
    icon: Wrench,
  },
  [INSTALLATION_STAGE.DONE]: {
    color: THEME_COLORS.success,
    icon: CheckCircle,
  },
};

const COLUMNS = INSTALLATION_STAGE_CONFIG.map((stage) => ({
  ...stage,
  ...STAGE_PRESENTATION[stage.key],
}));


function emptyBoard() {
  return Object.fromEntries(
    COLUMNS.map((column) => [column.key, { items: [], total: 0 }])
  );
}


function personName(person = {}) {
  return [person.firstName, person.lastName].filter(Boolean).join(" ")
    || person.email
    || "Non affecté";
}


export default function GmaoInstallationsV2() {
  const navigate = useNavigate();
  const requestVersionRef = useRef(0);
  const columnRequestRef = useRef({});

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [assignees, setAssignees] = useState([]);
  const [board, setBoard] = useState(emptyBoard);
  const [pages, setPages] = useState({});
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingColumns, setLoadingColumns] = useState({});
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createOptions, setCreateOptions] = useState({
    clients: [],
    prospects: [],
    availableMachines: [],
  });
  const [createOptionsLoading, setCreateOptionsLoading] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState("");
  const [machineCreateOpen, setMachineCreateOpen] = useState(false);
  const [machineCreateSaving, setMachineCreateSaving] = useState(false);
  const [machineCatalogItems, setMachineCatalogItems] = useState([]);
  const [machineCatalogError, setMachineCatalogError] = useState("");
  const [createdMachineId, setCreatedMachineId] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let active = true;
    gmaoInstallationWorkflow.getOptions()
      .then((payload) => {
        if (active) setAssignees(payload?.assignees || []);
      })
      .catch(() => {
        if (active) setAssignees([]);
      });
    return () => {
      active = false;
    };
  }, []);

  const openCreateModal = useCallback(async () => {
    setCreateOpen(true);
    setCreateError("");
    setCreatedMachineId("");
    setCreateOptionsLoading(true);

    try {
      const payload = await gmao.getInstallationOptions();
      setCreateOptions({
        clients: payload?.clients || [],
        prospects: payload?.prospects || [],
        availableMachines: payload?.availableMachines || [],
      });
    } catch (requestError) {
      setCreateOptions({
        clients: [],
        prospects: [],
        availableMachines: [],
      });
      setCreateError(
        formatApiError(requestError?.response?.data?.detail)
        || "Impossible de charger les données de création."
      );
    } finally {
      setCreateOptionsLoading(false);
    }
  }, []);

  const closeCreateModal = useCallback(() => {
    if (!createSaving) {
      setCreateOpen(false);
      setMachineCreateOpen(false);
      setCreateError("");
      setCreatedMachineId("");
    }
  }, [createSaving]);

  const createInstallationClient = useCallback(
    async (payload) => {
      const created =
        await gmao.createInstallationQuickClient(
          payload
        );

      setCreateOptions((current) => ({
        ...current,
        clients: [
          ...current.clients.filter(
            (item) =>
              item?.id !== created?.id
          ),
          created,
        ],
        prospects:
          current.prospects.filter(
            (item) =>
              item?.id !== created?.id
          ),
      }));

      return created;
    },
    []
  );

  const loadMachineCatalog = useCallback(
    async () => {
      setMachineCatalogError("");

      try {
        const items =
          await machineCatalogApi.list({
            type: "machine",
          });

        setMachineCatalogItems(
          Array.isArray(items)
            ? items
            : []
        );
      } catch {
        setMachineCatalogItems([]);
        setMachineCatalogError(
          "catalog_load_failed"
        );
      }
    },
    []
  );

  const openStockMachineCreate =
    useCallback(async () => {
      setMachineCreateOpen(true);
      await loadMachineCatalog();
    }, [loadMachineCatalog]);

  const closeStockMachineCreate =
    useCallback(() => {
      if (!machineCreateSaving) {
        setMachineCreateOpen(false);
      }
    }, [machineCreateSaving]);

  const saveStockMachine =
    useCallback(async (payload) => {
      setMachineCreateSaving(true);

      try {
        const created =
          await gmao.createMachine({
            ...payload,
            prospect_id: null,
            location_status: "in_stock",
          });

        setCreateOptions(
          (current) => ({
            ...current,
            availableMachines: [
              ...current.availableMachines
                .filter(
                  (item) =>
                    item?.id
                    !== created?.id
                ),
              created,
            ],
          })
        );

        setCreatedMachineId(
          created?.id || ""
        );
        setMachineCreateOpen(false);

        toast.success(
          "Machine ajoutée au stock et sélectionnée"
        );
      } catch (requestError) {
        toast.error(
          formatApiError(
            requestError
              ?.response
              ?.data
              ?.detail
          )
          || "Impossible d’ajouter la machine au stock."
        );
      } finally {
        setMachineCreateSaving(false);
      }
    }, []);

  const createManualInstallation = useCallback(async (payload) => {
    setCreateSaving(true);
    setCreateError("");

    try {
      const installation = await gmao.createInstallation(payload);
      setCreateOpen(false);
      toast.success("Installation créée");
      navigate(
        `/app/gmao/installations/${encodeURIComponent(installation.id)}`
      );
    } catch (requestError) {
      setCreateError(
        formatApiError(requestError?.response?.data?.detail)
        || "Impossible de créer l’installation."
      );
    } finally {
      setCreateSaving(false);
    }
  }, [navigate]);

  const assigneeById = useMemo(
    () => Object.fromEntries(assignees.map((assignee) => [assignee.id, assignee])),
    [assignees]
  );

  const requestParams = useCallback(
    (column, page) => ({
      ...column.query,
      search: debouncedSearch,
      technician_id: assigneeId,
      page,
      limit: PAGE_SIZE,
    }),
    [assigneeId, debouncedSearch]
  );

  const loadBoard = useCallback(async () => {
    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;

    // Invalide les anciennes requêtes de pagination lorsqu'un
    // nouveau filtre ou une nouvelle recherche recharge le board.
    columnRequestRef.current = {};

    setInitialLoading(true);
    setError("");
    setPages({});

    try {
      const payloads = await Promise.all(
        COLUMNS.map((column) => gmao.listInstallations(requestParams(column, 1)))
      );

      if (requestVersionRef.current !== requestVersion) return;

      setBoard(Object.fromEntries(
        COLUMNS.map((column, index) => [
          column.key,
          {
            items: payloads[index]?.items || [],
            total: Number(payloads[index]?.total || 0),
          },
        ])
      ));
    } catch (requestError) {
      if (requestVersionRef.current !== requestVersion) return;
      setBoard(emptyBoard());
      setError(
        formatApiError(requestError?.response?.data?.detail)
        || "Impossible de charger les installations."
      );
    } finally {
      if (requestVersionRef.current === requestVersion) {
        setInitialLoading(false);
      }
    }
  }, [requestParams]);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  const loadColumnPage = useCallback(async (column, page) => {
    const requestId = (columnRequestRef.current[column.key] || 0) + 1;
    columnRequestRef.current[column.key] = requestId;
    setLoadingColumns((current) => ({ ...current, [column.key]: true }));
    setError("");

    try {
      const payload = await gmao.listInstallations(requestParams(column, page));
      if (columnRequestRef.current[column.key] !== requestId) return;

      setBoard((current) => ({
        ...current,
        [column.key]: {
          items: payload?.items || [],
          total: Number(payload?.total || 0),
        },
      }));
      setPages((current) => ({ ...current, [column.key]: page }));
    } catch (requestError) {
      if (columnRequestRef.current[column.key] !== requestId) return;
      setError(
        formatApiError(requestError?.response?.data?.detail)
        || "Impossible de charger cette page d’installations."
      );
    } finally {
      if (columnRequestRef.current[column.key] === requestId) {
        setLoadingColumns((current) => ({ ...current, [column.key]: false }));
      }
    }
  }, [requestParams]);

  const totals = useMemo(
    () => Object.fromEntries(
      COLUMNS.map((column) => [column.key, board[column.key]?.total || 0])
    ),
    [board]
  );

  if (initialLoading && !Object.values(board).some((column) => column.total)) {
    return <main className="p-6 text-sm text-[var(--do-muted)]">Chargement des installations…</main>;
  }

  return (
    <main className="pipeline-viewport-page acta-responsive-pipeline acta-standard-page acta-gmao-page-standard w-full max-w-full min-w-0 p-4 sm:p-5 lg:p-8" data-testid="gmao-installations-v2-page">
      <section className="grid grid-cols-2 gap-3 xl:grid-cols-4" data-testid="gmao-installations-kpis">
        {COLUMNS.map((column) => {
          const Icon = column.icon;
          return (
            <article key={column.key} className="rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4">
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-semibold uppercase tracking-wide text-[var(--do-muted)]">{column.label}</span>
                <Icon size={18} style={{ color: column.color }} />
              </div>
              <strong className="mt-3 block text-3xl font-black text-[var(--do-text)]">{totals[column.key]}</strong>
            </article>
          );
        })}
      </section>

      <section className="mt-4 rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-3">
        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          <label className="relative min-w-0 flex-1">
            <MagnifyingGlass size={17} weight="bold" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--do-muted)]" />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Rechercher une installation…"
              data-testid="installation-search"
              className="h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] pl-10 pr-4 text-sm outline-none focus:border-[var(--do-accent)] focus:ring-2 focus:ring-[var(--do-accent-soft)]"
            />
          </label>

          <select
            value={assigneeId}
            onChange={(event) => setAssigneeId(event.target.value)}
            data-testid="installation-technician-filter"
            className="h-11 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 text-sm font-bold outline-none md:w-[280px]"
          >
            <option value="">Tous les intervenants</option>
            {assignees.map((assignee) => (
              <option key={assignee.id} value={assignee.id}>
                {personName(assignee)} · {assignee.role === "maintenance_manager" ? "Resp. maintenance" : "Technicien"}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={openCreateModal}
            data-testid="installation-create-open"
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[var(--do-accent)] px-4 text-sm font-heading font-bold text-[var(--do-on-accent)] transition hover:opacity-90"
          >
            <Plus size={18} weight="bold" />
            Nouvelle installation
          </button>
        </div>
      </section>

      {error && (
        <div className="mt-4 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4 text-sm font-semibold" role="alert">
          {error}
        </div>
      )}

      <section className="mt-4 grid min-w-0 grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-4" data-testid="installation-kanban-board">
        {COLUMNS.map((column) => (
          <InstallationKanbanColumn
            key={column.key}
            column={column}
            data={board[column.key] || { items: [], total: 0 }}
            currentPage={pages[column.key] || 1}
            pageSize={PAGE_SIZE}
            loading={Boolean(loadingColumns[column.key])}
            assigneeById={assigneeById}
            onOpen={(id) => navigate(`/app/gmao/installations/${encodeURIComponent(id)}`)}
            onPageChange={(page) => loadColumnPage(column, page)}
          />
        ))}
      </section>

      <InstallationCreateModal
        open={createOpen}
        clients={createOptions.clients}
        prospects={createOptions.prospects}
        machines={createOptions.availableMachines}
        selectedMachineId={createdMachineId}
        loading={createOptionsLoading}
        saving={createSaving}
        error={createError}
        onClose={closeCreateModal}
        onSubmit={createManualInstallation}
        onCreateClient={createInstallationClient}
        onRequestCreateMachine={openStockMachineCreate}
      />

      <MachineFormModal
        open={machineCreateOpen}
        mode="create"
        prospects={[]}
        machines={[]}
        catalogItems={machineCatalogItems}
        catalogLoadError={machineCatalogError}
        onReloadCatalog={loadMachineCatalog}
        fixedLocationStatus="in_stock"
        saving={machineCreateSaving}
        onClose={closeStockMachineCreate}
        onSubmit={saveStockMachine}
      />
    </main>
  );
}
